import { CaretRightOutlined, PauseOutlined } from "@ant-design/icons";
import { Watermark } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { authService } from "../../services";
import { APP_DOMAIN } from "../../utls/constants";
import "./index.less";

const FALLBACK_IMAGE = 'data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%22400%22%20height%3D%22400%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Crect%20width%3D%22100%25%22%20height%3D%22100%25%22%20fill%3D%22%232a2a2a%22%2F%3E%3Ctext%20x%3D%2250%25%22%20y%3D%2250%25%22%20font-family%3D%22sans-serif%22%20font-size%3D%2220%22%20text-anchor%3D%22middle%22%20fill%3D%22%23888%22%20dy%3D%22.3em%22%3EMedia%20Unavailable%3C%2Ftext%3E%3C%2Fsvg%3E';

interface VideoPlayerProps {
  videoUrl: string;
  thumbnailUrl: string;
  watermarkText?: string;
  classNameProp?: string;
  autoPlay?: boolean;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  videoUrl,
  thumbnailUrl,
  watermarkText = authService.getUser().username,
  classNameProp,
  autoPlay = false,
}) => {
  const [showThumbnail, setShowThumbnail] = useState<boolean>(true);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [showControls, setShowControls] = useState<boolean>(true);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [hasThumbnailError, setHasThumbnailError] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastControlsShowTimeRef = useRef<number>(0);

  // Format time for display
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Initialize the timestamp to prevent immediate play on first load
  useEffect(() => {
    lastControlsShowTimeRef.current = Date.now();
  }, []);

  // Reset error when thumbnail URL changes
  useEffect(() => {
    setHasThumbnailError(false);
  }, [thumbnailUrl]);

  const handleThumbnailClick = () => {
    setShowThumbnail(false);
    if (videoRef.current) {
      videoRef.current
        .play()
        .then(() => {
          setIsPlaying(true);
          showControlsTemporarily();
        })
        .catch((error) => console.error("Error playing video:", error));
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current
          .play()
          .then(() => {
            setIsPlaying(true);
            showControlsTemporarily();
          })
          .catch((error) => console.error("Error playing video:", error));
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
        showControlsTemporarily();
      }
    }
  };

  const handleVideoClick = () => {
    const now = Date.now();

    // If controls are hidden, show them
    if (!showControls) {
      showControlsTemporarily(true);
      lastControlsShowTimeRef.current = now;
      return;
    }

    // If controls are visible, hide them
    setShowControls(false);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
  };

  const handlePlayPauseButtonClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();

    togglePlay();

    // Show controls temporarily after play/pause
    showControlsTemporarily();
  };

  const showControlsTemporarily = (fromUserTap = false) => {
    setShowControls(true);

    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 2000);
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  const updateProgress = () => {
    if (videoRef.current && !isDragging) {
      const currentTime = videoRef.current.currentTime;
      const duration = videoRef.current.duration;
      const progress = (currentTime / duration) * 100;
      setProgress(progress);
      setCurrentTime(currentTime);
      setDuration(duration);
    }
  };

  const seekToPosition = (percentage: number) => {
    if (!videoRef.current || !duration) return;

    const seekTime = (percentage / 100) * duration;
    videoRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
    setProgress(percentage);
  };

  const seekVideo = (e: React.MouseEvent<HTMLDivElement>) => {
    if (progressBarRef.current && videoRef.current) {
      const rect = progressBarRef.current.getBoundingClientRect();
      const percentage = ((e.clientX - rect.left) / rect.width) * 100;
      seekToPosition(Math.max(0, Math.min(100, percentage)));
    }
  };

  // Touch/drag handlers for mobile sliding - using native DOM events to avoid passive listener issues
  const handleNativeTouchStart = (e: TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
    showControlsTemporarily();

    // Add haptic feedback on supported devices
    if ("vibrate" in navigator) {
      navigator.vibrate(10);
    }

    if (!progressBarRef.current || !duration) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    const touchX = e.touches[0].clientX - rect.left;
    const percentage = (touchX / rect.width) * 100;

    seekToPosition(Math.max(0, Math.min(100, percentage)));
  };

  const handleNativeTouchMove = (e: TouchEvent) => {
    if (!isDragging || !progressBarRef.current || !duration) return;

    e.preventDefault();
    e.stopPropagation();

    const rect = progressBarRef.current.getBoundingClientRect();
    const touchX = e.touches[0].clientX - rect.left;
    const percentage = (touchX / rect.width) * 100;

    seekToPosition(Math.max(0, Math.min(100, percentage)));
  };

  const handleNativeTouchEnd = (e: TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(false);
  };

  // Mouse drag handlers for desktop
  const handleProgressMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
    showControlsTemporarily();

    const handleMouseMove = (event: MouseEvent) => {
      if (!progressBarRef.current || !duration) return;

      const rect = progressBarRef.current.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const percentage = (mouseX / rect.width) * 100;

      seekToPosition(Math.max(0, Math.min(100, percentage)));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    // Handle initial click
    if (progressBarRef.current && videoRef.current) {
      const rect = progressBarRef.current.getBoundingClientRect();
      const percentage = ((e.clientX - rect.left) / rect.width) * 100;
      seekToPosition(Math.max(0, Math.min(100, percentage)));
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const handleTimeUpdate = () => updateProgress();
      const handlePlay = () => {
        setIsPlaying(true);
        showControlsTemporarily();
      };
      const handlePause = () => {
        setIsPlaying(false);
        showControlsTemporarily();
      };
      const handleLoadedMetadata = () => {
        setDuration(video.duration);
        setCurrentTime(video.currentTime);
      };

      video.addEventListener("timeupdate", handleTimeUpdate);
      video.addEventListener("play", handlePlay);
      video.addEventListener("pause", handlePause);
      video.addEventListener("loadedmetadata", handleLoadedMetadata);

      // Set up IntersectionObserver for lazy loading
      const handleIntersection = (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && video) {
            video.preload = "auto"; // Preload when video is in view
          } else if (video) {
            video.preload = "none"; // Stop loading when out of view
          }
        });
      };

      const observer = new IntersectionObserver(handleIntersection, {
        root: null,
        threshold: 0.25,
      });

      observer.observe(video);

      if (autoPlay) {
        setShowThumbnail(false);
        video
          .play()
          .then(() => {
            setIsPlaying(true);
            showControlsTemporarily();
          })
          .catch((error) => console.error("Error playing video:", error));
      }

      return () => {
        video.removeEventListener("timeupdate", handleTimeUpdate);
        video.removeEventListener("play", handlePlay);
        video.removeEventListener("pause", handlePause);
        video.removeEventListener("loadedmetadata", handleLoadedMetadata);
        if (controlsTimeoutRef.current) {
          clearTimeout(controlsTimeoutRef.current);
        }
        observer.disconnect();
      };
    }
  }, [autoPlay]);

  // Add native touch event listeners to progress bar to avoid passive listener issues
  useEffect(() => {
    const progressBar = progressBarRef.current;
    if (progressBar) {
      // Add touch event listeners with passive: false to allow preventDefault
      progressBar.addEventListener("touchstart", handleNativeTouchStart, { passive: false });
      progressBar.addEventListener("touchmove", handleNativeTouchMove, { passive: false });
      progressBar.addEventListener("touchend", handleNativeTouchEnd, { passive: false });

      return () => {
        progressBar.removeEventListener("touchstart", handleNativeTouchStart);
        progressBar.removeEventListener("touchmove", handleNativeTouchMove);
        progressBar.removeEventListener("touchend", handleNativeTouchEnd);
      };
    }
  }, [isDragging, duration]); // Re-run when dependencies change

  return (
    <div className="video-player" ref={videoContainerRef}>
      <div className="video-container">
        <video
          className={classNameProp ? classNameProp : ""}
          ref={videoRef}
          preload="none" // Prevent loading until user interaction or visible
          playsInline
          style={{ display: showThumbnail ? "none" : "block" }}
          onMouseMove={() => showControlsTemporarily()}
          onMouseDown={(e) => {
            // Don't handle if clicking on progress bar area or center button
            const target = e.target as HTMLElement;
            if (target.closest(".progress-bar") || target.closest(".center-play-button")) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }

            // Prevent default click behavior
            e.preventDefault();
            e.stopPropagation();

            // Handle the click logic (show/hide controls)
            handleVideoClick();
          }}
          onTouchEnd={(e) => {
            // Handle touch end for mobile tap behavior
            const target = e.target as HTMLElement;

            // Don't handle if touching progress bar area or center button
            if (target.closest(".progress-bar") || target.closest(".center-play-button")) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }

            // Prevent the click event from firing
            e.preventDefault();
            e.stopPropagation();

            //(show/hide controls)
            handleVideoClick();
          }}
          onTouchStart={(e) => {
            // Prevent video click when touching progress bar area or center button
            const target = e.target as HTMLElement;
            if (target.closest(".progress-bar") || target.closest(".center-play-button")) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onClick={(e) => {
            // Completely prevent default click behavior
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <source src={videoUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        {showThumbnail && (
          <div className="thumbnail-container" onClick={handleThumbnailClick}>
            <img 
              src={hasThumbnailError ? FALLBACK_IMAGE : thumbnailUrl} 
              alt="video thumbnail" 
              className="thumbnail-image" 
              onError={() => setHasThumbnailError(true)}
            />
            <CaretRightOutlined className="play-icon" />
          </div>
        )}
        <Watermark
          content={[watermarkText, `www.${APP_DOMAIN}`]}
          className="video-watermark"
          font={{ fontSize: 16, color: "rgba(255, 255, 255, 0.5)" }}
        />

        {!showThumbnail && (
          <div
            className="center-play-button"
            onClick={handlePlayPauseButtonClick}
            onTouchEnd={handlePlayPauseButtonClick}
            style={{
              opacity: showControls ? 1 : 0,
              pointerEvents: showControls ? "auto" : "none",
            }}
          >
            {isPlaying ? <PauseOutlined /> : <CaretRightOutlined />}
          </div>
        )}

        {!showThumbnail && (
          <div className={`controls ${!isPlaying ? "isPause" : ""} ${showControls ? "show" : "hide"}`}>
            <div
              className="progress-bar"
              ref={progressBarRef}
              onClick={seekVideo}
              onMouseDown={handleProgressMouseDown}
              style={{ cursor: "pointer", position: "relative" }}
            >
              <div className="progress" style={{ width: `${progress}%` }}></div>
              {/* Progress handle/scrubber */}
              <div
                className="progress-handle"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: `${progress}%`,
                  transform: "translate(-50%, -50%)",
                  width: "14px",
                  height: "14px",
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  border: "1px solid rgba(0, 0, 0, 0.2)",
                  borderRadius: "50%",
                  opacity: isDragging ? 1 : 0,
                  transition: "opacity 0.2s ease-in-out",
                  pointerEvents: "none",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
                }}
              />
              {/* Time tooltip when dragging */}
              {isDragging && duration > 0 && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "25px",
                    left: `${progress}%`,
                    transform: "translateX(-50%)",
                    backgroundColor: "rgba(0, 0, 0, 0.8)",
                    color: "white",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    whiteSpace: "nowrap",
                    pointerEvents: "none",
                    zIndex: 10,
                  }}
                >
                  {formatTime(currentTime)} / {formatTime(duration)}
                </div>
              )}
            </div>
            <div className="buttons">
              <button
                className="control-button"
                onClick={toggleMute}
                aria-label={isMuted ? "Unmute video" : "Mute video"}
                title={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted ? (
                  <img
                    src="https://img.icons8.com/?size=100&id=7913&format=png&color=FFFFFF"
                    alt="Muted"
                    style={{
                      width: "18px",
                      height: "18px",
                      filter: "drop-shadow(0 1px 3px rgba(0, 0, 0, 0.4))",
                    }}
                  />
                ) : (
                  <img
                    src="https://img.icons8.com/?size=100&id=vI9d0s6ZRxxX&format=png&color=FFFFFF"
                    alt="Unmuted"
                    style={{
                      width: "18px",
                      height: "18px",
                      filter: "drop-shadow(0 1px 3px rgba(0, 0, 0, 0.4))",
                    }}
                  />
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoPlayer;
